import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../../config/api';
import { uploadFilesWithProgress } from '../../../utils/uploadFilesWithProgress';
import PresignedImage from '../../../components/common/PresignedImage';
import {
  addEventExpense,
  deleteEventExpense,
  fetchEventExpenses,
  updateEventExpense,
} from '../services/eventService';
import {
  formatShareMoney as formatMoney,
  formatSignedShareMoney,
  sortShareBalances,
  findMyShareBalance,
  isSettlementEntry,
  settlementReceiver,
} from '../utils/expenseShareUtils';
import { downloadExpenseShareCsv, downloadExpenseSharePdf } from '../utils/expenseShareExport';
import { expenseFieldEnabled, expenseFieldRequired } from '../constants/expenseFields';

const inputClass = 'w-full rounded-lg border border-amber-200 dark:border-amber-900/50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500';
const labelClass = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const personName = (p) => p?.userName || p?.name || 'Someone';
const personLabel = (p) => {
  const name = personName(p);
  return p?.external ? `${name} (external)` : name;
};

const equalPercents = (count) => {
  if (count <= 0) return [];
  const each = Math.round((100 / count) * 100) / 100;
  const values = Array.from({ length: count }, () => each);
  const used = each * (count - 1);
  values[count - 1] = Math.round((100 - used) * 100) / 100;
  return values;
};

const ExpenseSharePanel = ({
  eventId,
  currentUserId,
  isCreator = false,
  configuration = {},
  participants = [],
  readOnly = false,
  mode = 'list',
  onAddEntry,
  onEditEntry,
  onSettle,
  onSaved,
  initialEntry = null,
  eventName = '',
}) => {
  const currency = configuration.currency || 'INR';
  const imageEnabled = configuration.fields?.image
    ? expenseFieldEnabled(configuration.fields, 'image')
    : true;
  const imageRequired = imageEnabled && expenseFieldRequired(configuration.fields, 'image');
  const people = useMemo(() => {
    const byId = new Map();
    (participants || []).forEach((p) => {
      if (p?.userId && !byId.has(String(p.userId))) {
        byId.set(String(p.userId), {
          userId: p.userId,
          userName: personName(p),
          external: Boolean(p.external || String(p.userId).startsWith('ext-')),
        });
      }
    });
    return Array.from(byId.values());
  }, [participants]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [item, setItem] = useState('');
  const [amount, setAmount] = useState('');
  const [paidByUserId, setPaidByUserId] = useState(currentUserId || '');
  const [paidToUserId, setPaidToUserId] = useState('');
  const [usedIds, setUsedIds] = useState(() => people.map((p) => String(p.userId)));
  const [percents, setPercents] = useState({});
  const [showStatus, setShowStatus] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFileName, setImageFileName] = useState('');
  const [exporting, setExporting] = useState('');
  const fileRef = useRef(null);

  const redistribute = (ids) => {
    const values = equalPercents(ids.length);
    const next = {};
    ids.forEach((id, index) => {
      next[id] = String(values[index]);
    });
    setPercents(next);
  };

  useEffect(() => {
    if (!people.length) return;
    setUsedIds((prev) => {
      const allowed = new Set(people.map((p) => String(p.userId)));
      const kept = prev.filter((id) => allowed.has(id));
      const next = kept.length ? kept : people.map((p) => String(p.userId));
      if (!initialEntry) {
        const values = equalPercents(next.length);
        const map = {};
        next.forEach((id, index) => {
          map[id] = String(values[index]);
        });
        setPercents(map);
      }
      if (!paidByUserId && currentUserId) setPaidByUserId(currentUserId);
      if (paidByUserId && !people.some((p) => sameId(p.userId, paidByUserId))) {
        setPaidByUserId(people[0].userId);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people.map((p) => p.userId).join(',')]);

  useEffect(() => {
    if (!initialEntry) return;
    setItem(initialEntry.item || '');
    setAmount(initialEntry.amount != null ? String(initialEntry.amount) : '');
    setPaidByUserId(initialEntry.paidByUserId || initialEntry.userId || currentUserId || '');
    setPaidToUserId(settlementReceiver(initialEntry)?.userId || '');
    setImageUrl(initialEntry.imageUrl || '');
    setImageFileName(initialEntry.imageFileName || '');
    const shares = Array.isArray(initialEntry.shares) ? initialEntry.shares : [];
    const ids = shares.map((share) => String(share.userId || '')).filter(Boolean);
    if (ids.length) {
      setUsedIds(ids);
      const map = {};
      shares.forEach((share) => {
        if (share?.userId != null) map[String(share.userId)] = String(share.percent ?? '');
      });
      setPercents(map);
    }
  }, [initialEntry, currentUserId]);

  const loadEntries = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await fetchEventExpenses(eventId);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'form') return;
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mode]);

  const toggleUsed = (userId) => {
    const id = String(userId);
    setUsedIds((prev) => {
      const has = prev.includes(id);
      const next = has ? prev.filter((row) => row !== id) : [...prev, id];
      if (!next.length) return prev;
      redistribute(next);
      return next;
    });
  };

  const percentSum = usedIds.reduce((sum, id) => sum + (Number(percents[id]) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const value = Number(amount);
    if (!item.trim()) {
      setError('Item is required');
      return;
    }
    if (!Number.isFinite(value) || value < 0) {
      setError('Amount is required');
      return;
    }
    if (!paidByUserId) {
      setError('Select who paid');
      return;
    }
    if (!usedIds.length) {
      setError('Select at least one person who used this');
      return;
    }
    if (imageRequired && !imageUrl) {
      setError('Bill image is required');
      return;
    }
    if (Math.abs(percentSum - 100) > 0.5) {
      setError('Percentages must add up to 100');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const paidPerson = people.find((p) => sameId(p.userId, paidByUserId));
      const payload = {
        item: item.trim(),
        amount: value,
        paidByUserId,
        paidByUserName: paidPerson?.userName,
        imageUrl: imageUrl || undefined,
        imageFileName: imageFileName || undefined,
        usedBy: usedIds.map((id) => {
          const person = people.find((p) => sameId(p.userId, id));
          return {
            userId: id,
            userName: person?.userName,
            percent: Number(percents[id]),
          };
        }),
      };
      if (initialEntry?.id) {
        await updateEventExpense(eventId, initialEntry.id, payload);
      } else {
        await addEventExpense(eventId, payload);
      }
      setItem('');
      setAmount('');
      setImageUrl('');
      setImageFileName('');
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleSettleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const value = Number(amount);
    if (!paidByUserId) {
      setError('Select who paid');
      return;
    }
    if (!paidToUserId) {
      setError('Select who received the payment');
      return;
    }
    if (sameId(paidByUserId, paidToUserId)) {
      setError('Payer and receiver must be different');
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      setError('Amount is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const paidPerson = people.find((p) => sameId(p.userId, paidByUserId));
      const payload = {
        entryKind: 'SETTLEMENT',
        item: item.trim() || 'Settlement',
        amount: value,
        paidByUserId,
        paidByUserName: paidPerson?.userName,
        paidToUserId,
        imageUrl: imageUrl || undefined,
        imageFileName: imageFileName || undefined,
      };
      if (initialEntry?.id) {
        await updateEventExpense(eventId, initialEntry.id, payload);
      } else {
        await addEventExpense(eventId, payload);
      }
      setItem('');
      setAmount('');
      setImageUrl('');
      setImageFileName('');
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not record settlement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    try {
      await deleteEventExpense(eventId, entry.id);
      setEntries((prev) => prev.filter((row) => row.id !== entry.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete entry');
    }
  };

  const handleImage = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files: [file],
      });
      const data = results[0]?.data;
      if (!data?.fileUrl) throw new Error('Upload failed');
      setImageUrl(data.fileUrl);
      setImageFileName(data.fileName || file.name);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Image upload failed');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExport = async (kind) => {
    if (!entries.length || exporting) return;
    setExporting(kind);
    setError('');
    try {
      const payload = { eventName, currency, entries, people };
      if (kind === 'csv') downloadExpenseShareCsv(payload);
      else await downloadExpenseSharePdf(payload);
    } catch (err) {
      setError(err?.message || 'Could not export');
    } finally {
      setExporting('');
    }
  };

  const totals = useMemo(() => {
    const net = new Map();
    people.forEach((p) => net.set(String(p.userId), { name: p.userName, paid: 0, used: 0 }));
    let total = 0;
    entries.forEach((entry) => {
      const amt = Number(entry.amount) || 0;
      if (!isSettlementEntry(entry)) total += amt;
      const payerId = String(entry.paidByUserId || entry.userId || '');
      if (payerId && net.has(payerId)) net.get(payerId).paid += amt;
      (entry.shares || []).forEach((share) => {
        const id = String(share.userId || '');
        if (id && net.has(id)) net.get(id).used += Number(share.amount) || 0;
      });
    });
    const balances = Array.from(net.entries()).map(([userId, row]) => ({
      userId,
      userName: row.name,
      name: row.name,
      net: Math.round((row.paid - row.used) * 100) / 100,
    }));
    return { total, balances };
  }, [entries, people]);

  const myBalance = findMyShareBalance(totals.balances, currentUserId);
  const sortedBalances = sortShareBalances(totals.balances);
  const fullySettled = entries.length > 0 && totals.balances.every((row) => Math.abs(Number(row.net) || 0) <= 0.004);

  useEffect(() => {
    if (mode !== 'settle' || initialEntry) return;
    const owed = totals.balances.filter((row) => Number(row.net) > 0.004);
    const owes = totals.balances.filter((row) => Number(row.net) < -0.004);
    const me = findMyShareBalance(totals.balances, currentUserId);
    let fromId = currentUserId || people[0]?.userId || '';
    let toId = people.find((p) => !sameId(p.userId, fromId))?.userId || '';
    let suggested = '';
    if (me && Number(me.net) < -0.004 && owed[0]) {
      fromId = String(currentUserId);
      toId = owed[0].userId;
      suggested = String(Math.min(Math.abs(Number(me.net)), Number(owed[0].net)));
    } else if (owes[0] && owed[0]) {
      fromId = owes[0].userId;
      toId = owed[0].userId;
      suggested = String(Math.min(Math.abs(Number(owes[0].net)), Number(owed[0].net)));
    }
    setPaidByUserId(fromId || '');
    setPaidToUserId(toId || '');
    if (suggested) setAmount(suggested);
  }, [mode, initialEntry, totals.balances, currentUserId, people]);

  const form = (
        <form onSubmit={handleSubmit} className={`flex flex-col ${mode === 'form' ? 'h-full min-h-0' : 'space-y-2.5 rounded-lg bg-white/80 dark:bg-gray-900/40 p-3 border border-amber-100 dark:border-amber-900/40'}`}>
          <div className={`space-y-3 ${mode === 'form' ? 'flex-1 min-h-0 overflow-y-auto pr-1' : ''}`}>
          <label className={labelClass}>
            Item
            <input value={item} onChange={(e) => setItem(e.target.value)} className={inputClass} placeholder="Dinner, cab, groceries…" />
          </label>
          <label className={labelClass}>
            Amount
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} placeholder="0.00" />
          </label>
          <label className={labelClass}>
            Who paid
            <select value={paidByUserId} onChange={(e) => setPaidByUserId(e.target.value)} className={inputClass}>
              {people.map((p) => (
                <option key={p.userId} value={p.userId}>{personLabel(p)}</option>
              ))}
            </select>
          </label>
          <div className={mode === 'form' ? 'flex-1 min-h-0 flex flex-col' : ''}>
            <p className={labelClass}>Used by</p>
            <div className={`space-y-1.5 ${mode === 'form' ? 'flex-1 overflow-y-auto' : 'max-h-48 overflow-y-auto'}`}>
              {people.map((p) => {
                const id = String(p.userId);
                const checked = usedIds.includes(id);
                return (
                  <label key={id} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUsed(p.userId)}
                      className="rounded"
                    />
                    <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{personLabel(p)}</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      disabled={!checked}
                      value={checked ? (percents[id] ?? '') : ''}
                      onChange={(e) => setPercents((prev) => ({ ...prev, [id]: e.target.value }))}
                      className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-1.5 py-1 disabled:opacity-40"
                    />
                    <span className="text-[10px] text-gray-500">%</span>
                  </label>
                );
              })}
            </div>
            <p className={`mt-1 text-[11px] font-semibold ${Math.abs(percentSum - 100) > 0.5 ? 'text-rose-600' : 'text-gray-500'}`}>
              {usedIds.length} using · {percentSum.toFixed(2)}% of 100%
            </p>
          </div>
          {imageEnabled && (
          <div>
            <p className={labelClass}>Bill image{imageRequired ? ' *' : ''}</p>
            {imageUrl ? (
              <div className="flex items-center gap-2">
                <PresignedImage fileKey={imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <button type="button" className="text-xs text-rose-600 font-semibold" onClick={() => { setImageUrl(''); setImageFileName(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleImage(e.target.files)} className="text-xs" />
            )}
          </div>
          )}
          {error && <p className="text-xs text-rose-600">{error}</p>}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="mt-3 w-full rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-3 disabled:opacity-60"
          >
            {saving ? 'Saving…' : initialEntry?.id ? 'Save changes' : 'Save entry'}
          </button>
        </form>
  );

  const settleForm = (
    <form onSubmit={handleSettleSubmit} className="flex flex-col h-full min-h-0">
      <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1">
        <p className="text-xs text-gray-500">
          Record money already paid. Balances update immediately; when everyone is even, settlement is complete.
        </p>
        <label className={labelClass}>
          Who paid
          <select value={paidByUserId} onChange={(e) => setPaidByUserId(e.target.value)} className={inputClass}>
            {people.map((p) => (
              <option key={p.userId} value={p.userId}>{personLabel(p)}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Paid to
          <select value={paidToUserId} onChange={(e) => setPaidToUserId(e.target.value)} className={inputClass}>
            <option value="">Select person</option>
            {people.filter((p) => !sameId(p.userId, paidByUserId)).map((p) => (
              <option key={p.userId} value={p.userId}>{personLabel(p)}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Amount
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} placeholder="0.00" />
        </label>
        <label className={labelClass}>
          Note
          <input value={item === 'Settlement' ? '' : item} onChange={(e) => setItem(e.target.value)} className={inputClass} placeholder="UPI, cash, bank transfer…" />
        </label>
        {imageEnabled && (
          <div>
            <p className={labelClass}>Payment screenshot</p>
            {imageUrl ? (
              <div className="flex items-center gap-2">
                <PresignedImage fileKey={imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <button type="button" className="text-xs text-rose-600 font-semibold" onClick={() => { setImageUrl(''); setImageFileName(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleImage(e.target.files)} className="text-xs" />
            )}
          </div>
        )}
        {error && <p className="text-xs text-rose-600">{error}</p>}
      </div>
      <button
        type="submit"
        disabled={saving}
        className="mt-3 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-3 disabled:opacity-60"
      >
        {saving ? 'Saving…' : initialEntry?.id ? 'Save settlement' : 'Mark paid'}
      </button>
    </form>
  );

  if (mode === 'settle') {
    return settleForm;
  }

  if (mode === 'form') {
    return form;
  }

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Shared expenses</p>
          <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
            Total {formatMoney(totals.total, currency)}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {entries.length > 0 && (
            <>
              <button
                type="button"
                disabled={Boolean(exporting)}
                onClick={() => handleExport('pdf')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/40 disabled:opacity-50"
              >
                {exporting === 'pdf' ? 'PDF…' : 'PDF'}
              </button>
              <button
                type="button"
                disabled={Boolean(exporting)}
                onClick={() => handleExport('csv')}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/40 disabled:opacity-50"
              >
                {exporting === 'csv' ? 'CSV…' : 'CSV'}
              </button>
              <button
                type="button"
                onClick={() => setShowStatus((open) => !open)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/40"
              >
                {showStatus ? 'Hide status' : 'Check status'}
              </button>
            </>
          )}
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={onSettle}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
              >
                Settle
              </button>
              <button
                type="button"
                onClick={onAddEntry}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
              >
                Add entry
              </button>
            </>
          )}
        </div>
      </div>

      {fullySettled && (
        <p className="text-center text-sm font-semibold text-emerald-700 dark:text-emerald-300 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 py-2">
          Settlement complete
        </p>
      )}

      {entries.length > 0 && myBalance && (
        <div className="text-center py-1">
          {myBalance.net > 0.004 ? (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">You are owed</p>
              <p className="text-3xl font-black tabular-nums text-emerald-600 dark:text-emerald-400 leading-tight">
                {formatSignedShareMoney(myBalance.net, currency)}
              </p>
            </>
          ) : myBalance.net < -0.004 ? (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">You owe</p>
              <p className="text-3xl font-black tabular-nums text-rose-600 dark:text-rose-400 leading-tight">
                {formatSignedShareMoney(myBalance.net, currency)}
              </p>
            </>
          ) : (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Your status</p>
              <p className="text-2xl font-black text-gray-500 dark:text-gray-400 leading-tight">Settled</p>
            </>
          )}
        </div>
      )}

      {showStatus && sortedBalances.length > 0 && (
        <div className="rounded-lg bg-white/80 dark:bg-gray-900/50 p-2.5 space-y-1.5">
          {sortedBalances.map((row) => {
            const net = Number(row.net) || 0;
            const name = row.userName || row.name || 'Someone';
            const owed = net > 0.004;
            const owes = net < -0.004;
            return (
              <div key={row.userId} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate font-medium text-gray-800 dark:text-gray-200">{name}</span>
                <span className={`tabular-nums font-bold ${
                  owed ? 'text-emerald-600 dark:text-emerald-400' : owes ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400'
                }`}>
                  {owes || owed ? formatSignedShareMoney(net, currency) : formatMoney(0, currency)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-500 text-center py-3">Loading expenses…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-3">No shared expenses yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((row) => {
            const canManage = !readOnly && (sameId(row.userId, currentUserId) || isCreator);
            const settlement = isSettlementEntry(row);
            const receiver = settlementReceiver(row);
            return (
              <li key={row.id} className={`rounded-lg bg-white dark:bg-gray-900 border p-2.5 ${
                settlement ? 'border-emerald-200 dark:border-emerald-900/50' : 'border-gray-100 dark:border-gray-700'
              }`}>
                <div className="flex items-start justify-between gap-2">
                  {row.imageUrl && (
                    <PresignedImage fileKey={row.imageUrl} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    {settlement ? (
                      <>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Settlement</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {row.paidByUserName || row.userName || 'Someone'} paid {receiver?.userName || 'someone'}
                          {row.amount != null ? ` ${formatMoney(row.amount, currency)}` : ''}
                        </p>
                        {row.item && row.item !== 'Settlement' && (
                          <p className="text-[11px] text-gray-500 mt-0.5">{row.item}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.item || 'Expense'}</p>
                        <p className="text-[11px] text-gray-500">
                          Paid by {row.paidByUserName || row.userName || 'Someone'}
                          {row.amount != null ? ` · ${formatMoney(row.amount, currency)}` : ''}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {(row.shares || []).map((share) => `${share.userName || 'User'} ${Number(share.percent || 0).toFixed(1)}%`).join(' · ')}
                        </p>
                      </>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <button type="button" onClick={() => onEditEntry?.(row)} className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                        Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(row)} className="text-[11px] font-semibold text-rose-600">
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ExpenseSharePanel;
